import { apis } from '../../../core/services/params';
import axiosInstance from '../../../core/services/axiosConfig';
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Typography,
    Card,
    Tag,
    Spin,
    Divider,
    Button,
    Avatar,
    Descriptions,
    Timeline,
    Alert
} from "antd";
import {
    ArrowLeftOutlined,
    UserOutlined,
    PhoneOutlined,
    SyncOutlined,
    MessageOutlined,
    FileTextOutlined,
    PaperClipOutlined,
    DownloadOutlined
} from "@ant-design/icons";
import moment from "moment";
import StitchLayout from "../../components/Crm/CrmLayout";
import { getTicketStatusLabel, getTicketStatusColor, getTicketPriorityLabel, getTicketPriorityColor, getSourceLabel } from "./crmConstants";
import ItemHistoryModal, { ItemHistoryBadge } from "../../components/Crm/ItemHistoryModal";
import WhatsAppConversationModal from "../../components/Crm/WhatsAppConversationModal";

const { Title, Text } = Typography;

// --- Interfaces ---

interface Conversation {
    id: number;
    body: string;
    body_text: string;
    incoming: boolean;
    private: boolean;
    user_id: number;
    support_email: string | null;
    created_at: string;
    from_email: string;
    to_emails: string[];
    attachments_v2: any[];
}

interface RequesterInfo {
    id: number;
    name: string;
    email: string | null;
    mobile: string | null;
    phone: string | null;
}

// Moglix platform user info shape from user_info field
interface UserInfo {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    codEnabled: boolean;
    deviceCreated: string;
}

interface Ticket {
    id: number;
    subject: string;
    description: string;
    description_text: string;
    status: number;
    priority: number;
    created_at: string;
    updated_at: string;
    due_by: string;
    fr_due_by: string;
    is_escalated: boolean;
    source: number;
    group_id: number;
    requester_id: number;
    responder_id: number | null;
    to_emails: string[] | null;
    ticket_cc_emails: string[];
    reply_cc_emails: string[];
    spam: boolean;
    conversations: Conversation[];
    requester_info: RequesterInfo;
    custom_fields: Record<string, any>;
    tags: string[];
    user_info?: UserInfo;
    attachments_v2?: Array<{
        id: number;
        name: string;
        content_type: string;
        size: number;
        attachment_url: string;
        presigned_url?: string;
        created_at: string;
        updated_at: string;
    }>;
}

const FreshdeskTicketDetails = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [ticket, setTicket] = useState<Ticket | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeItemId, setActiveItemId] = useState<string | null>(null);
    const [whatsappModal, setWhatsappModal] = useState<{ visible: boolean; phone?: string }>({ visible: false });
    const [ozonetelCalls, setOzonetelCalls] = useState<any[]>([]);
    const [callsLoading, setCallsLoading] = useState(false);

    useEffect(() => {
        const fetchTicketDetails = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const response = await axiosInstance.get(`${apis.FRESHDESK_TICKET_DETAILS}?ticket_id=${id}`);
                const data = response.data;
                setTicket(data.data || data);
            } catch (err) {
                console.error(err);
                setError("Could not load ticket details.");
            } finally {
                setLoading(false);
            }
        };

        fetchTicketDetails();
    }, [id]);

    useEffect(() => {
        const fetchOzonetelCalls = async (phone: string) => {
            setCallsLoading(true);
            try {
                let e164Phone = phone.replace(/^\+91/, '').trim();
                if (!e164Phone.startsWith('+')) {
                    e164Phone = `+91${e164Phone}`;
                }
                const response = await axiosInstance.post(
                    `${apis.OZONETEL_LIVE_REPORT}?page=1&per_page=10`,
                    { filters: { E164: e164Phone } }
                );
                const data = response.data;
                if (data?.data) {
                    setOzonetelCalls(data.data);
                }
            } catch (err) {
                console.error("Error fetching Ozonetel calls", err);
            } finally {
                setCallsLoading(false);
            }
        };

        if (ticket) {
            const phone = ticket.user_info?.phone || ticket.requester_info?.phone || ticket.requester_info?.mobile;
            if (phone) {
                fetchOzonetelCalls(phone);
            }
        }
    }, [ticket]);

    if (loading) {
        return (
            <StitchLayout>
                <div className="flex justify-center items-center h-screen">
                    <Spin size="large" tip="Loading ticket details..." />
                </div>
            </StitchLayout>
        );
    }

    if (error || !ticket) {
        return (
            <StitchLayout>
                <div className="p-8 text-center">
                    <Alert message="Error" description={error || "Ticket not found"} type="error" showIcon />
                    <Button type="primary" className="mt-4" onClick={() => navigate("/crm/freshdesk-tickets")}>
                        Back to Tickets
                    </Button>
                </div>
            </StitchLayout>
        );
    }

    return (
        <>
            <StitchLayout>
                <div className="max-w-[1600px] mx-auto p-6 space-y-6">
                    {/* Header Navigation */}
                    <div className="flex items-center gap-4">
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate("/crm/freshdesk-tickets")}
                            type="text"
                            className="hover:bg-gray-100"
                        />
                        <div>
                            <div className="flex items-center gap-3 flex-wrap">
                                <Title level={3} style={{ marginBottom: 0 }}>
                                    #{ticket.id} {ticket.subject}
                                </Title>
                                {/* Status tag resolved via shared TICKET_STATUS_MAP */}
                                <Tag color={getTicketStatusColor(ticket.status)} className="ml-2 text-sm px-3 py-0.5">
                                    {getTicketStatusLabel(ticket.status)}
                                </Tag>
                                {/* Priority tag resolved via shared PRIORITY_MAP */}
                                <Tag color={getTicketPriorityColor(ticket.priority)} className="text-sm px-3 py-0.5">
                                    {getTicketPriorityLabel(ticket.priority)} Priority
                                </Tag>
                                {ticket.is_escalated && <Tag color="red">Escalated</Tag>}
                                {/* WhatsApp Chat History — shown for any ticket with a phone number */}
                                {(ticket.user_info?.phone || ticket.requester_info?.phone || ticket.requester_info?.mobile) && (
                                    <button
                                        onClick={() => setWhatsappModal({
                                            visible: true,
                                            phone: ticket.user_info?.phone || ticket.requester_info?.phone || ticket.requester_info?.mobile || ''
                                        })}
                                        className="flex items-center gap-1.5 ml-1 pl-3 border-l border-gray-200 text-green-600 hover:text-green-800 transition-colors cursor-pointer"
                                    >
                                        <span className="text-base">💬</span>
                                        <span className="text-[12px] font-semibold">View WhatsApp Chat</span>
                                    </button>
                                )}
                                {/* Order Item ID — inline in the same header row */}
                                {ticket.custom_fields?.order_item_id && (
                                    <div className="flex items-center gap-1.5 ml-1 pl-3 border-l border-gray-200">
                                        <span className="text-[11px] text-gray-400 font-semibold uppercase tracking-wider">Order Item:</span>
                                        <ItemHistoryBadge
                                            itemId={String(ticket.custom_fields.order_item_id)}
                                            onClick={setActiveItemId}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="text-gray-500 mt-1 flex gap-4 text-sm">
                                <span>Created: {moment(ticket.created_at).format("MMM DD, YYYY HH:mm")}</span>
                                {ticket.updated_at && <span>Updated: {moment(ticket.updated_at).format("MMM DD, YYYY HH:mm")}</span>}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                        {/* Left Column: Description & Conversations */}
                        <div className="xl:col-span-2 space-y-6">

                            {/* Description Card */}
                            <Card title={<><FileTextOutlined /> Description</>} className="shadow-sm rounded-xl">
                                <div
                                    className="prose max-w-none text-gray-700"
                                    dangerouslySetInnerHTML={{ __html: ticket.description }}
                                />
                            </Card>

                            {/* Attachments Card */}
                            {ticket.attachments_v2 && ticket.attachments_v2.length > 0 && (
                                <Card title={<><PaperClipOutlined /> Attachments ({ticket.attachments_v2.length})</>} className="shadow-sm rounded-xl">
                                    <div className="flex flex-col gap-3">
                                        {ticket.attachments_v2.map((attachment) => (
                                            <div key={attachment.id} className="flex justify-between items-center p-3 bg-gray-50 border border-gray-100 rounded-lg">
                                                <div className="flex items-center gap-3 overflow-hidden">
                                                    <PaperClipOutlined className="text-gray-400 text-lg flex-shrink-0" />
                                                    <div className="flex flex-col min-w-0">
                                                        <a
                                                            href={attachment.presigned_url || attachment.attachment_url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="text-sm font-medium text-blue-600 hover:underline truncate"
                                                            title={attachment.name}
                                                        >
                                                            {attachment.name}
                                                        </a>
                                                        <span className="text-xs text-gray-500">
                                                            {(attachment.size / 1024).toFixed(1)} KB &bull; {attachment.content_type.split('/')[1] || attachment.content_type}
                                                        </span>
                                                    </div>
                                                </div>
                                                <Button
                                                    type="text"
                                                    icon={<DownloadOutlined />}
                                                    className="text-gray-500 hover:text-blue-600"
                                                    title="Download"
                                                    onClick={() => window.open(attachment.presigned_url || attachment.attachment_url, '_blank')}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </Card>
                            )}

                            {/* Conversation History */}
                            <Card title={<><MessageOutlined /> Conversation History</>} className="shadow-sm rounded-xl">
                                {ticket.conversations && ticket.conversations.length > 0 ? (
                                    <Timeline>
                                        {ticket.conversations.map((conv) => (
                                            <Timeline.Item
                                                key={conv.id}
                                                color={conv.incoming ? "blue" : "green"}
                                                dot={conv.incoming ? <UserOutlined /> : <SyncOutlined />}
                                            >
                                                <div className={`p-4 rounded-lg border ${conv.incoming ? 'bg-blue-50 border-blue-100' : 'bg-green-50 border-green-100'} mb-4`}>
                                                    <div className="flex justify-between items-start mb-2">
                                                        <div>
                                                            <span className="font-semibold text-gray-800">
                                                                {conv.from_email || "System"}
                                                            </span>
                                                            <span className="text-xs text-gray-500 ml-2">
                                                                {moment(conv.created_at).format("MMM DD, HH:mm")}
                                                            </span>
                                                        </div>
                                                        <Tag>{conv.incoming ? "Customer" : "Support"}</Tag>
                                                    </div>
                                                    <div
                                                        className="text-gray-700 text-sm overflow-hidden"
                                                        dangerouslySetInnerHTML={{ __html: conv.body }}
                                                    />
                                                    {conv.attachments_v2 && conv.attachments_v2.length > 0 && (
                                                        <div className="mt-3 pt-2 border-t border-gray-200/50">
                                                            <Text type="secondary" className="text-xs">Attachments: {conv.attachments_v2.length} file(s)</Text>
                                                        </div>
                                                    )}
                                                </div>
                                            </Timeline.Item>
                                        ))}
                                    </Timeline>
                                ) : (
                                    <div className="text-center py-8 text-gray-400">No conversation history</div>
                                )}
                            </Card>
                        </div>

                        {/* Right Column: Meta Info & Attributes */}
                        <div className="space-y-6">

                            {/* Requester Info — compact one-liner */}
                            <Card title="Requester Info" size="small" className="shadow-sm rounded-xl">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <Avatar size={32} icon={<UserOutlined />} className="bg-blue-100 text-blue-600 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <span className="font-semibold text-gray-800 text-sm">{ticket.requester_info?.name || "Unknown User"}</span>
                                        {ticket.requester_info?.email && (
                                            <span className="text-gray-400 text-xs ml-2">{ticket.requester_info.email}</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-gray-500 flex-shrink-0">
                                        {(ticket.requester_info?.phone || ticket.requester_info?.mobile) && (
                                            <a
                                                href={`/crm/interaction-timeline?phone_number=${(ticket.requester_info.phone || ticket.requester_info.mobile || '').replace(/^\+91/, '')}`}
                                                target="_blank" rel="noreferrer"
                                                className="flex items-center gap-1 text-blue-600 hover:underline cursor-pointer"
                                            >
                                                <PhoneOutlined />{ticket.requester_info.phone || ticket.requester_info.mobile}
                                            </a>
                                        )}
                                        <span className="flex items-center gap-1"><UserOutlined />ID: {ticket.requester_id}</span>
                                    </div>
                                </div>
                            </Card>

                            {/* Moglix user info card shown when user_info is present */}
                            {ticket.user_info && (
                                <Card
                                    title={
                                        <span className="flex items-center gap-2">
                                            <UserOutlined className="text-indigo-500" />
                                            <span>Moglix User Info</span>
                                        </span>
                                    }
                                    size="small"
                                    className="shadow-sm rounded-xl border-indigo-100"
                                >
                                    <div className="flex flex-col gap-1.5">
                                        {/* Row 1: ID · Name · Phone */}
                                        <div className="flex items-center gap-3 flex-wrap text-sm">
                                            <a
                                                href={`/crm/customer/${ticket.user_info.id}`}
                                                target="_blank" rel="noreferrer"
                                                className="font-bold text-indigo-600 hover:underline font-mono"
                                            >
                                                #{ticket.user_info.id}
                                            </a>
                                            <span className="font-medium text-gray-800">
                                                {ticket.user_info.firstName} {ticket.user_info.lastName}
                                            </span>
                                            {ticket.user_info.phone && (
                                                <a
                                                    href={`/crm/interaction-timeline?phone_number=${ticket.user_info.phone.replace(/^\+91/, '')}`}
                                                    target="_blank" rel="noreferrer"
                                                    className="flex items-center gap-1 text-xs text-blue-600 hover:underline cursor-pointer"
                                                >
                                                    <PhoneOutlined />{ticket.user_info.phone}
                                                </a>
                                            )}
                                        </div>
                                        {/* Row 2: Email · Device · COD */}
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {ticket.user_info.email && (
                                                <span className="text-xs text-gray-400 truncate">{ticket.user_info.email}</span>
                                            )}
                                            {ticket.user_info.deviceCreated && (
                                                <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full border border-gray-200 font-medium">
                                                    {ticket.user_info.deviceCreated}
                                                </span>
                                            )}
                                            {ticket.user_info.codEnabled && (
                                                <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full border border-amber-200 font-semibold">
                                                    COD Enabled
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            )}

                            {/* Ticket Properties — compact */}
                            <Card title="Ticket Properties" size="small" className="shadow-sm rounded-xl">
                                <div className="flex flex-col gap-1.5 text-xs">
                                    {/* Row 1: Group ID · Source */}
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="text-gray-400">Group:</span>
                                        <span className="font-mono text-gray-700">{ticket.group_id}</span>
                                        <span className="text-gray-300">·</span>
                                        <span className="text-gray-400">Source:</span>
                                        <span className="font-medium text-gray-700">{getSourceLabel(ticket.source)}</span>
                                    </div>
                                    {/* Row 2: Due By · FR Due By */}
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="text-gray-400">Due:</span>
                                        <span className={`font-medium ${moment(ticket.due_by).isBefore(moment()) ? 'text-red-500' : 'text-gray-700'}`}>
                                            {moment(ticket.due_by).format('MMM DD, HH:mm')}
                                        </span>
                                        <span className="text-gray-300">·</span>
                                        <span className="text-gray-400">FR Due:</span>
                                        <span className="font-medium text-gray-700">{moment(ticket.fr_due_by).format('MMM DD, HH:mm')}</span>
                                    </div>
                                </div>
                            </Card>

                            {/* Custom Fields (Dynamic) */}
                            {ticket.custom_fields && (
                                <Card title="Custom Fields" size="small" className="shadow-sm rounded-xl">
                                    <div className="flex flex-col gap-3">
                                        {Object.entries(ticket.custom_fields).map(([key, value]) => {
                                            // Skip null/empty values
                                            if (!value) return null;
                                            // Skip low-signal / redundant fields
                                            const SKIP_KEYS = new Set([
                                                'cf_escalation',        // already shown as header tag
                                                'cf_facebook',          // not relevant
                                                'cf_facebook737239',    // not relevant
                                                'cf_query',             // duplicate of cf_base_query
                                                'cf_calling_group',     // internal routing
                                                'cf_fileds',            // typo field, unused
                                                'cf_issue',             // usually null
                                                'cf_language',          // usually null
                                                'cf_missed_calls',      // internal
                                                'cf_return_team',       // internal
                                                'cf_sentiments',        // internal
                                                'cf_sub_concern',       // superseded by cf_sub_concern_1
                                                'cf_communication_channel',
                                                'assigned_to',
                                                'reply_due',
                                                'cf_agent_name',
                                                'cf_base_query',
                                                'cf_chatbot_customer_name',
                                                'cf_phonechatbot',
                                                'cf_department',
                                            ]);
                                            if (SKIP_KEYS.has(key)) return null;
                                            // Skip noise values
                                            const str = String(value).trim().toLowerCase();
                                            if (str === 'no' || str === 'na' || str === 'n/a') return null;

                                            const label = key.replace(/^cf_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                                            // Order Item ID — render as a clickable badge that opens the item history popup
                                            if (key === 'order_item_id') {
                                                return (
                                                    <div key={key} className="flex items-center justify-between border-b border-gray-50 last:border-0 pb-2 last:pb-0">
                                                        <span className="text-xs text-gray-400 uppercase tracking-wider">{label}</span>
                                                        <ItemHistoryBadge
                                                            itemId={String(value)}
                                                            onClick={setActiveItemId}
                                                        />
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div key={key} className="flex flex-col border-b border-gray-50 last:border-0 pb-2 last:pb-0">
                                                    <span className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">{label}</span>
                                                    <span className="text-sm font-medium text-gray-700">{String(value)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Card>
                            )}

                            {/* Tags */}
                            {ticket.tags && ticket.tags.length > 0 && (
                                <Card title="Tags" size="small" className="shadow-sm rounded-xl">
                                    <div className="flex flex-wrap gap-2">
                                        {ticket.tags.map(tag => (
                                            <Tag key={tag} color="geekblue">{tag}</Tag>
                                        ))}
                                    </div>
                                </Card>
                            )}

                            {/* Call Track History */}
                            {(callsLoading || ozonetelCalls.length > 0) && (
                                <Card
                                    title={
                                        <span className="flex items-center gap-2">
                                            <PhoneOutlined className="text-blue-500" />
                                            <span>Call Track History</span>
                                        </span>
                                    }
                                    size="small"
                                    className="shadow-sm rounded-xl border-blue-100"
                                >
                                    {callsLoading ? (
                                        <div className="flex justify-center p-4">
                                            <Spin size="small" />
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-3">
                                            {ozonetelCalls.map((call) => {
                                                // Extract UCID from custom fields dynamically to handle any casing (e.g. cf_ucid, cf_UCID, ucid)
                                                let ticketUcid = '';
                                                if (ticket.custom_fields) {
                                                    const ucidKey = Object.keys(ticket.custom_fields).find(k => k.toLowerCase().includes('ucid'));
                                                    if (ucidKey) ticketUcid = String(ticket.custom_fields[ucidKey] || '');
                                                }
                                                const callUcidStr = String(call.UCID || call.ucid || '').trim();

                                                const isMatchedByTicket = call.freshdesk_ticket_id && String(call.freshdesk_ticket_id) === String(ticket.id);
                                                const isMatchedByUcid = !!ticketUcid && !!callUcidStr && callUcidStr === ticketUcid.trim();
                                                const isMatched = isMatchedByTicket || isMatchedByUcid;
                                                return (
                                                    <div key={call.UCID} className={`flex justify-between items-center border-b border-gray-50 last:border-0 pb-2 last:pb-0 ${isMatched ? 'bg-indigo-50 -mx-3 px-3 py-2 rounded-lg border-indigo-100' : ''}`}>
                                                        <div className="flex flex-col">
                                                            <div className="flex items-center gap-2">
                                                                <span
                                                                    className={`text-sm font-medium cursor-pointer hover:underline ${isMatched ? 'text-indigo-700 font-bold' : 'text-blue-600'}`}
                                                                    onClick={() => window.open(`/crm/ozonetel-call-track/${call.UCID}`, '_blank')}
                                                                >
                                                                    {call.UCID}
                                                                </span>
                                                                {isMatched && <Tag color="indigo" className="m-0 text-[10px] px-1.5 py-0 border-0 leading-tight rounded-sm">Current Call</Tag>}
                                                            </div>
                                                            <span className={`text-xs ${isMatched ? 'text-indigo-400' : 'text-gray-400'}`}>
                                                                {moment(call.StartTime).format('MMM DD, HH:mm')}
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-col items-end">
                                                            <span className="text-xs font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                                                                {call.CallDuration}
                                                            </span>
                                                            <Tag color={call.Status?.toLowerCase() === 'answered' ? 'green' : 'red'} style={{ margin: '4px 0 0 0' }} className="text-[10px] px-1.5 py-0 border-0 leading-tight">
                                                                {call.Status || 'Unknown'}
                                                            </Tag>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </Card>
                            )}

                        </div>
                    </div>
                </div>
            </StitchLayout>

            {/* Item history popup — opened by clicking item ID badge in Custom Fields */}
            <ItemHistoryModal
                itemId={activeItemId}
                onClose={() => setActiveItemId(null)}
            />
            {/* WhatsApp conversation modal — opened for Portal+ChatBot tickets */}
            <WhatsAppConversationModal
                visible={whatsappModal.visible}
                phone={whatsappModal.phone}
                onClose={() => setWhatsappModal({ visible: false })}
            /></>
    );
};

export default FreshdeskTicketDetails;
